// ── 매입 붙여넣기 유틸 (TSV 파싱 · 정규화 · 미리보기 상태) ────────────────────
// 서버(merge.js / paste.service.js)의 정규화·coreKey 와 동일 규칙으로 맞춘다.
//   coreKey = 구분|사용일|업체|금액 (후속칸 비고·비고2·세금계산서·발급일은 식별에서 제외).
//   미리보기 상태: 신규(new) / 갱신(update, 이미 있음→덮어씀) / 오류(error).

export const PASTE_COLUMNS = [
  { key: 'kind', label: '구분', width: 56 },
  { key: 'use_date', label: '사용일', width: 96 },
  { key: 'vendor', label: '업체', width: 150 },
  { key: 'amount', label: '금액', width: 96, align: 'right' },
  { key: 'note', label: '비고', width: 180 },
  { key: 'note2', label: '비고2', width: 120 },
  { key: 'tax_invoice_amount', label: '세금계산서', width: 96, align: 'right' },
  { key: 'tax_invoice_issued_date', label: '발급일', width: 96 },
];

const HEADER_ALIASES = {
  '구분': 'kind', '사용일': 'use_date', '업체': 'vendor', '금액': 'amount',
  '비고': 'note', '비고2': 'note2', '세금계산서': 'tax_invoice_amount',
  '발급일': 'tax_invoice_issued_date', '발급일(전송일자로)': 'tax_invoice_issued_date',
};

export function emptyRow() {
  const r = {};
  PASTE_COLUMNS.forEach((c) => { r[c.key] = ''; });
  return r;
}

export function isRowEmpty(row) {
  return PASTE_COLUMNS.every((c) => String(row[c.key] || '').trim() === '');
}

// ── 정규화 (서버 parse-excel 규칙과 동일) ─────────────────────────────────────
export function normText(v) {
  return String(v == null ? '' : v).trim();
}
export function normDate(v) {
  const s = normText(v);
  if (!s) return '';
  const m = /^(\d{4})\s*[.\-/]\s*(\d{1,2})\s*[.\-/]\s*(\d{1,2})/.exec(s);
  return m ? `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}` : '';
}
export function normAmount(v) {
  if (v == null || v === '') return null;
  let s = String(v).replace(/[,\s원₩]/g, '').trim();
  if (!s) return null;
  const paren = /^\(([0-9.-]+)\)$/.exec(s);
  if (paren) s = `-${paren[1]}`;
  const n = Number(s);
  return Number.isFinite(n) ? Math.round(n) : null;
}

export function coreKey(row) {
  const amt = normAmount(row.amount);
  return [normText(row.kind), normDate(row.use_date), normText(row.vendor), amt == null ? '' : String(amt)].join('|');
}

// ── 클립보드 파싱 ─────────────────────────────────────────────────────────────
export function parseClipboardMatrix(rawText) {
  return String(rawText || '')
    .replace(/\r\n/g, '\n')
    .split('\n')
    .filter((line) => line.length > 0)
    .map((line) => line.split('\t'));
}

export function isHeaderRow(cells) {
  const set = (cells || []).map((c) => normText(c));
  return set.includes('구분') && set.includes('사용일') && set.includes('금액');
}

// 헤더 있으면 이름 매핑, 없으면 고정 8열 순서로 행 객체 배열 생성
export function matrixToRows(matrix) {
  if (!matrix || matrix.length === 0) return [];
  const colMap = {}; // colIndex → key
  let startIdx = 0;
  if (isHeaderRow(matrix[0])) {
    matrix[0].forEach((cell, i) => {
      const key = HEADER_ALIASES[normText(cell)];
      if (key && !Object.values(colMap).includes(key)) colMap[i] = key;
    });
    startIdx = 1;
  } else {
    PASTE_COLUMNS.forEach((c, i) => { colMap[i] = c.key; });
  }
  const rows = [];
  for (let i = startIdx; i < matrix.length; i += 1) {
    const cells = matrix[i];
    if (!cells || cells.every((c) => normText(c) === '')) continue;
    const row = emptyRow();
    Object.entries(colMap).forEach(([idx, key]) => {
      row[key] = normText(cells[Number(idx)]);
    });
    rows.push(row);
  }
  return rows;
}

// ── 행 오류 판정 ──────────────────────────────────────────────────────────────
export function rowError(row) {
  if (!normDate(row.use_date)) return '사용일 형식 오류 (예: 2024-07-03)';
  if (normAmount(row.amount) == null) return '금액 오류';
  return '';
}

// ── 미리보기 상태 (신규/갱신/오류) + 요약. existingRows 와 멀티셋 비교. ─────────
export function buildStatuses(rows, existingRows) {
  const existingCount = new Map();
  for (const e of (existingRows || [])) {
    const k = coreKey(e);
    existingCount.set(k, (existingCount.get(k) || 0) + 1);
  }
  const consumed = new Map();
  const statuses = rows.map((row) => {
    if (isRowEmpty(row)) return { status: 'empty', message: '' };
    const err = rowError(row);
    if (err) return { status: 'error', message: err };
    const k = coreKey(row);
    const used = consumed.get(k) || 0;
    const exist = existingCount.get(k) || 0;
    consumed.set(k, used + 1);
    return { status: used < exist ? 'update' : 'new', message: '' };
  });
  const summary = statuses.reduce((a, s) => {
    if (s.status === 'error') a.errorCount += 1;
    else if (s.status === 'update') a.updateCount += 1;
    else if (s.status === 'new') a.newCount += 1;
    return a;
  }, { newCount: 0, updateCount: 0, errorCount: 0 });
  return { statuses, summary };
}
